import pluralize from 'pluralize';
import { useCallback } from 'react';

import { useTrackCanvasMediaImported } from '@air/analytics';
import { useToasts } from '@air/provider-toast';

import { useEditCanvasStore } from '../store';
import { type AppNode, type EditUploadContentType } from '../types';
import { getVideoUploadRejectionMessage } from '../utils/canvasVideoUploadLimits';
import { classifyAcceptedUploadFiles } from '../utils/classifyAcceptedUploadFiles';
import { correctNodeDimensions } from '../utils/correctNodeDimensions';
import { getCompatibleImageUrl } from '../utils/getCompatibleImage';
import { getLocalImageDimensions } from '../utils/getLocalImageDimensions';
import { computeRowAwareAppendPosition } from '../utils/layout/nodeLayout';
import { partitionDroppedUploadFiles, type VideoEditUploadContentType } from '../utils/uploadMimeTypes';
import { commands, useCommandHistory } from './commands';
import { useNodeLayout } from './useNodeLayout';
import { useUploadAndPreload } from './useUploadAndPreload';
import { useZoomToFit } from './useZoomToFit';

export const FALLBACK_IMAGE_SIZE = 1024;

type ValidFileBase = {
  file: File;
  dimensions: { height: number; width: number };
  dimensionsKnown: boolean;
};

type ValidImageFile = ValidFileBase & {
  contentType: Exclude<EditUploadContentType, VideoEditUploadContentType>;
  mediaKind: 'image';
};

type ValidVideoFile = ValidFileBase & {
  blobUrl: string;
  contentType: VideoEditUploadContentType;
  duration: number;
  mediaKind: 'video';
};

type ValidFile = ValidImageFile | ValidVideoFile;

/** Total width of a single row laying out the given files side by side with `gap` between each. */
const getRowWidth = (files: ValidFile[], gap: number) => {
  if (files.length === 0) {
    return 0;
  }

  const totalImageWidth = files.reduce((sum, vf) => sum + vf.dimensions.width, 0);

  return totalImageWidth + gap * (files.length - 1);
};

type FileMeta = ValidFile & {
  blobUrl: string;
  nodeId: string;
  position: { x: number; y: number };
  previewId: string;
  zIndex: number;
};

const getSuccessToastMessage = (finalNodes: AppNode[]): string => {
  const imageCount = finalNodes.filter((n) => n.type === 'image').length;
  const videoCount = finalNodes.filter((n) => n.type === 'video').length;

  if (finalNodes.length === 1) {
    return videoCount === 1 ? 'Video added to Canvas' : 'Image added to Canvas';
  }

  if (imageCount > 0 && videoCount > 0) {
    return `${finalNodes.length} items added to Canvas`;
  }

  if (videoCount > 0) {
    return `${finalNodes.length} videos added to Canvas`;
  }

  return `${finalNodes.length} images added to Canvas`;
};

/**
 * Returns a callback that accepts raw File objects, uploads them, and adds
 * them as image or video nodes on the canvas with optimistic preview placeholders.
 *
 * This is the core upload-to-canvas logic shared by:
 * - {@link useUploadMediaToCanvas} (toolbar file picker)
 * - {@link EditCanvasFileDropzone} (OS file drag-and-drop)
 */
export const useDropFilesToCanvas = () => {
  const execute = useCommandHistory((state) => state.execute);
  const { showToast } = useToasts();
  const { getContentNodeRects, getGap, getMaxRowWidth } = useNodeLayout();
  const { uploadAndPreload } = useUploadAndPreload();
  const { trackCanvasMediaImported } = useTrackCanvasMediaImported();
  const { zoomToFit } = useZoomToFit();

  const dropFilesToCanvas = useCallback(
    async (files: File[], anchorPosition?: { x: number; y: number }) => {
      // Snapshot before upload: a pending null-state outcome takes over once the first image
      // node lands, so our success toast would just be noise under the outcome's own UI.
      const { nodes: nodesAtDrop, nullStateSelectedOutcomeId } = useEditCanvasStore.getState();
      const willNullStateOutcomeRun = nullStateSelectedOutcomeId !== null && nodesAtDrop.length === 0;
      const { accepted, unsupportedCount } = partitionDroppedUploadFiles(files);
      const { uploadFiles, videoRejections } = await classifyAcceptedUploadFiles(accepted);
      const validFiles: ValidFile[] = [];

      for (const upload of uploadFiles) {
        if (upload.mediaKind === 'video') {
          validFiles.push({
            blobUrl: upload.blobUrl,
            contentType: upload.contentType,
            dimensions: upload.dimensions,
            dimensionsKnown: true,
            duration: upload.duration,
            file: upload.file,
            mediaKind: 'video',
          });
        } else {
          const dimensions = await getLocalImageDimensions(upload.file);
          validFiles.push({
            contentType: upload.contentType,
            dimensions: dimensions ?? { height: FALLBACK_IMAGE_SIZE, width: FALLBACK_IMAGE_SIZE },
            dimensionsKnown: dimensions !== null,
            file: upload.file,
            mediaKind: 'image',
          });
        }
      }

      if (validFiles.length === 0) {
        if (videoRejections.length > 0) {
          showToast(getVideoUploadRejectionMessage(videoRejections), { color: 'red' });
        } else {
          showToast('No supported images or videos selected', { color: 'red' });
        }

        return;
      }

      const virtualRects = getContentNodeRects();
      const maxZIndex = useEditCanvasStore.getState().nodes.reduce((max, n) => Math.max(max, n.zIndex ?? 0), -1);
      const gap = getGap();
      const maxRowWidth = getMaxRowWidth();

      // When an anchor is provided (e.g. right-click "File Upload"), lay the batch
      // out in a single row centered on the anchor point instead of appending to
      // existing content.
      const anchorRowWidth = anchorPosition ? getRowWidth(validFiles, gap) : 0;
      let anchorCursorX = anchorPosition ? anchorPosition.x - anchorRowWidth / 2 : 0;

      const fileMeta: FileMeta[] = [];

      for (let i = 0; i < validFiles.length; i++) {
        const vf = validFiles[i];

        let position: { x: number; y: number };

        if (anchorPosition) {
          position = { x: anchorCursorX, y: anchorPosition.y - vf.dimensions.height / 2 };
          anchorCursorX += vf.dimensions.width + gap;
        } else {
          position = computeRowAwareAppendPosition({
            allNodes: virtualRects,
            gap,
            maxRowWidth,
            newNodeSize: vf.dimensions,
          }).position;
        }

        // Accumulate into virtualRects so each subsequent file positions after the previous ones.
        virtualRects.push({
          height: vf.dimensions.height,
          id: crypto.randomUUID(),
          width: vf.dimensions.width,
          x: position.x,
          y: position.y,
        });

        fileMeta.push({
          ...vf,
          blobUrl: vf.mediaKind === 'video' ? vf.blobUrl : URL.createObjectURL(vf.file),
          nodeId: crypto.randomUUID(),
          position,
          previewId: crypto.randomUUID(),
          zIndex: maxZIndex + 1 + i,
        });
      }

      const previewNodes: AppNode[] = fileMeta.map((meta) => ({
        data: { blobUrl: meta.blobUrl, fileName: meta.file.name, mediaKind: meta.mediaKind },
        id: meta.previewId,
        position: meta.position,
        selected: true,
        style: { height: meta.dimensions.height, width: meta.dimensions.width },
        type: 'canvasMediaUploadPreview',
        zIndex: meta.zIndex,
      }));

      const previewIds = new Set(previewNodes.map((n) => n.id));

      useEditCanvasStore.getState().addNodes(previewNodes);

      try {
        zoomToFit({ nodeIds: previewNodes.map((n) => n.id) });

        const results = await uploadAndPreload(fileMeta.map((m) => ({ contentType: m.contentType, file: m.file })));

        const finalNodes: AppNode[] = [];
        let uploadFailCount = 0;

        for (let i = 0; i < fileMeta.length; i++) {
          const result = results[i];
          const meta = fileMeta[i];

          if (result.status === 'fulfilled') {
            if (meta.mediaKind === 'video') {
              finalNodes.push({
                data: {
                  duration: meta.duration,
                  fileName: meta.file.name,
                  media: { original: result.cdnUrl, video: result.cdnUrl },
                  source: 'manual-edit',
                  type: 'video',
                },
                id: meta.nodeId,
                position: meta.position,
                style: { height: meta.dimensions.height, width: meta.dimensions.width },
                type: 'video',
                zIndex: meta.zIndex,
              });
            } else {
              finalNodes.push({
                data: {
                  fileName: meta.file.name,
                  media: { image: getCompatibleImageUrl(result.cdnUrl), original: result.cdnUrl },
                  source: 'manual-edit',
                  type: 'image',
                },
                id: meta.nodeId,
                position: meta.position,
                style: { height: meta.dimensions.height, width: meta.dimensions.width },
                type: 'image',
                zIndex: meta.zIndex,
              });
            }
          } else {
            uploadFailCount++;
          }
        }

        // Single command so all uploaded media are one undo/redo entry.
        if (finalNodes.length > 0) {
          execute(commands.addNodes(finalNodes, { removeNodeIds: Array.from(previewIds) }));

          const canvasId = useEditCanvasStore.getState().canvasId;

          if (canvasId) {
            trackCanvasMediaImported({
              canvas_id: canvasId,
              number_of_media: finalNodes.length,
              source: 'desktop',
            });
          }
        }

        // Fire-and-forget: correct dimensions for image files the browser couldn't decode locally
        const nodesNeedingDimensions = fileMeta
          .map((m, i) => ({ meta: m, result: results[i] }))
          .filter(
            (entry): entry is { meta: FileMeta; result: { status: 'fulfilled'; cdnUrl: string } } =>
              entry.meta.mediaKind === 'image' && !entry.meta.dimensionsKnown && entry.result.status === 'fulfilled',
          );

        if (nodesNeedingDimensions.length > 0) {
          correctNodeDimensions(
            nodesNeedingDimensions.map(({ meta, result }) => ({ cdnUrl: result.cdnUrl, nodeId: meta.nodeId })),
            finalNodes.map((n) => n.id),
          );
        }

        const totalFailed = unsupportedCount + uploadFailCount;

        if (finalNodes.length === 0) {
          showToast('Failed to upload media', { color: 'red' });
          useEditCanvasStore.setState((state) => ({
            nodes: state.nodes.filter((n) => !previewIds.has(n.id)),
          }));
        } else if (totalFailed > 0) {
          showToast(`${pluralize('file', totalFailed, true)} couldn't be added`, { color: 'red' });
        } else if (!willNullStateOutcomeRun) {
          showToast(getSuccessToastMessage(finalNodes));
        }

        if (videoRejections.length > 0) {
          showToast(getVideoUploadRejectionMessage(videoRejections), { color: 'red' });
        }
      } catch {
        showToast('Failed to upload media', { color: 'red' });
        // Remove preview nodes on failure (not synced, so bypass removeNodes to avoid queue)
        useEditCanvasStore.setState((state) => ({
          nodes: state.nodes.filter((n) => !previewIds.has(n.id)),
        }));
      }
    },
    [
      execute,
      getContentNodeRects,
      getGap,
      getMaxRowWidth,
      showToast,
      trackCanvasMediaImported,
      uploadAndPreload,
      zoomToFit,
    ],
  );

  return { dropFilesToCanvas };
};
